import {
  Dimensions,
  Gutter,
  Parapet,
  Roof,
  Wall,
  Configuration,
  WoodWall,
  ElementWall,
  SlidingGlassWallOptions,
  OptionsMaterial,
  Params,
  DEFAULT_SLIDING_GLASS_WALL_OPTIONS,
} from "src/types/configuration";
import { UiWall } from "src/components/Tabs/WallsTab/components/WallSelector";
import { getWallPosition } from "src/helpers/walls";
import CalculationService from "src/services/calculationService";
// @ts-ignore
import cloneDeep from "lodash/cloneDeep";

class ConfigurationService {
  private static instance: ConfigurationService;
  private calculationService?: CalculationService;
  private roofType?: string;
  private columns?: number;
  private dimensions?: Dimensions;
  private optionsMaterial?: OptionsMaterial;
  private params?: Params;
  private parapet?: Parapet;
  private roof?: Roof;
  private gutter?: Gutter;
  private walls?: Wall[];
  private storages: { left: boolean; right: boolean } = {
    left: false,
    right: false,
  };

  constructor(
    configuration: Configuration,
    calculationService: CalculationService
  ) {
    if (ConfigurationService.instance) {
      return ConfigurationService.instance;
    }

    this.calculationService = calculationService;
    this.roofType = configuration.roofType;
    this.columns = configuration.columns;
    this.dimensions = configuration.dimensions;
    this.parapet = configuration.parapet;
    this.gutter = configuration.gutter;
    this.roof = configuration.roof;
    this.walls = configuration.walls;
    this.storages = configuration.storages;
    this.optionsMaterial = configuration.optionsMaterial;
    this.params = configuration.params;

    ConfigurationService.instance = this;
  }

  public getRoofType(): string {
    if (!this.roofType) {
      throw new Error("RoofType is not set yet.");
    }

    return this.roofType;
  }

  public getDimensions(): Dimensions {
    if (!this.dimensions) {
      throw new Error("Dimensions are not set yet.");
    }

    return this.dimensions as Dimensions;
  }

  public getParapet(): Parapet {
    if (!this.parapet) {
      throw new Error("Parapet is not set yet.");
    }

    return this.parapet as Parapet;
  }

  public getGutter(): Gutter {
    if (!this.gutter) {
      throw new Error("Gutter is not set yet.");
    }

    return this.gutter as Gutter;
  }

  public getRoof(): Roof {
    if (!this.roof) {
      throw new Error("Roof is not set yet.");
    }

    return this.roof as Roof;
  }

  public getWalls(): Wall[] {
    return this.walls ?? [];
  }

  public getStorages() {
    return this.storages;
  }

  public getDefaultWoodWall(): WoodWall {
    return {
      side: "left",
      innerColor: "empty",
      outerColor: "empty",
      rotate: false,
      window: "empty",
      door: "empty",
      doorSide: "right",
    };
  }

  getDefaultSlidingGlassWallOptions(): SlidingGlassWallOptions {
    return { ...DEFAULT_SLIDING_GLASS_WALL_OPTIONS };
  }

  public getDefaultElementWall(): ElementWall {
    return {
      side: "left",
      innerColor: "empty",
      outerColor: "empty",
      elements: [],
    };
  }

  public getDefaultWall(
    wallId: string,
    inner: boolean = false,
    isRightSide: boolean = false
  ): Wall {
    return {
      id: wallId,
      position: getWallPosition(wallId),
      inner: inner,
      isRightSide: isRightSide,
      storage: "empty",
      slidingGlassWall: "empty",
      slidingGlassWallOptions: this.getDefaultSlidingGlassWallOptions(),
      woodWall: this.getDefaultWoodWall(),
      elementWall: this.getDefaultElementWall(),
    };
  }

  public setState(state: Configuration): void {
    this.roofType = cloneDeep(state.roofType);
    this.columns = cloneDeep(state.columns);
    this.dimensions = cloneDeep(state.dimensions);
    this.parapet = cloneDeep(state.parapet);
    this.gutter = cloneDeep(state.gutter);
    this.roof = cloneDeep(state.roof);
    this.walls = cloneDeep(state.walls);
    this.storages = cloneDeep(state.storages);
    this.optionsMaterial = cloneDeep(state.optionsMaterial);
    this.params = cloneDeep(state.params);
  }

  public setRoofType(roofType: string): void {
    this.roofType = roofType;
  }

  public setDimensions(dimensions: Dimensions): void {
    this.dimensions = dimensions;
  }

  public setOptionsMaterial(optionsMaterial: OptionsMaterial): void {
    this.optionsMaterial = optionsMaterial;
  }

  public setParapet(parapet: Parapet): void {
    this.parapet = parapet;
  }

  public setRoof(roof: Roof): void {
    this.roof = roof;
  }

  public setGutter(gutter: Gutter): void {
    this.gutter = gutter;
  }

  public setWalls(walls: Wall[]): void {
    this.walls = walls;
  }

  public setStorages(storages: { left: boolean; right: boolean }): void {
    this.storages = storages;
  }

  public getOptionsMaterial() {
    return this.optionsMaterial;
  }

  public findWall(wallId: string, inner: boolean = false): Wall {
    const foundWall = this.getWalls().find((wall) => wall.id === wallId);

    return foundWall ? { ...foundWall } : this.getDefaultWall(wallId, inner);
  }

  public findWallbyUiWall(selectedWall: UiWall | null) {
    if (!selectedWall) {
      return this.getDefaultWall("");
    }

    const foundWall = this.getWalls().find(
      (wall) => wall.id === selectedWall.id
    );

    return foundWall
      ? { ...foundWall }
      : this.getDefaultWall(selectedWall.id, selectedWall.inner);
  }

  public getConfiguration() {
    return {
      roofType: this.roofType,
      columns: this.columns,
      dimensions: this.dimensions,
      roof: this.roof,
      parapet: this.parapet,
      gutter: this.gutter,
      walls: this.walls,
      storages: this.storages,
      optionsMaterial: this.optionsMaterial,
      params: this.params,
      total_price: this.calculationService?.calculateTotalPrice() ?? 0,
    };
  }

  public getConfigurationHash() {
    return btoa(JSON.stringify(this.getConfiguration()));
  }

  public getMontage(): string {
    return this.optionsMaterial?.montage ? 'Geselecteerd' : 'n.v.t.';
  }
}

export default ConfigurationService;
